import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '../../hooks/useDebounce';
import { useProductsService, useCustomersService } from '../../providers/ApiProvider';
import { SearchIcon } from '../icons';
import { ADMIN_ROUTES } from '@conai/shared-constants';
import { useI18n } from '../../i18n';
import { extractList } from '../../utils/apiList';
import { getImageUrl } from '../../utils/image';

interface SearchResultItem {
  id: string;
  label: string;
  subtitle?: string;
  type: 'product' | 'customer';
  searchValue: string;
  /** Ảnh sản phẩm (đã qua getImageUrl) — không có thì hiện ô chữ cái như cũ. */
  imageUrl?: string;
}

export function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { t } = useI18n();

  const productsService = useProductsService();
  const customersService = useCustomersService();

  const {
    data: productsData,
    isLoading: productsLoading,
    error: productsError,
  } = useQuery({
    queryKey: ['global-search-products', debouncedQuery],
    queryFn: () => productsService.search(debouncedQuery),
    enabled: debouncedQuery.length >= 1,
    staleTime: 30 * 1000,
  });

  const {
    data: customersData,
    isLoading: customersLoading,
    error: customersError,
  } = useQuery({
    queryKey: ['global-search-customers', debouncedQuery],
    queryFn: () => customersService.search(debouncedQuery),
    enabled: debouncedQuery.length >= 1,
    staleTime: 30 * 1000,
  });

  const isLoading = productsLoading || customersLoading;
  const hasQuery = debouncedQuery.length >= 1;
  // Bug #5: trước đây lỗi bị nuốt hoàn toàn — /customers/ trả 403 vì thiếu quyền
  // thì ô tìm kiếm vẫn chỉ ghi "No results", không ai biết là do lỗi.
  const hasError = Boolean(productsError && customersError);

  // Bug #5: chỉ đọc `.results` là hỏng. Interceptor của api-client bóc envelope,
  // nếu BE không trả `meta` thì kết quả về đây là MẢNG TRẦN → `.results` undefined
  // → "No results" dù API có dữ liệu. extractList chịu được mọi hình dạng payload.
  const productResults = extractList<Record<string, unknown>>(productsData).map((product) => {
    const imageUrls = Array.isArray(product.image_urls) ? product.image_urls : [];
    const rawImage =
      (typeof product.image_url === 'string' ? product.image_url : '') ||
      (typeof imageUrls[0] === 'string' ? (imageUrls[0] as string) : '');

    return {
      id: String(product.id ?? ''),
      label: String(product.name ?? ''),
      subtitle: `SKU: ${String(product.sku ?? '—')}`,
      type: 'product' as const,
      searchValue: String(product.name ?? ''),
      // Bug #5: dropdown chỉ vẽ ô chữ "P", không hề hiển thị ảnh sản phẩm.
      imageUrl: rawImage ? getImageUrl(rawImage) : undefined,
    };
  });

  const customerResults = extractList<Record<string, unknown>>(customersData).map((customer) => ({
    id: String(customer.id ?? ''),
    label: String(customer.full_name ?? customer.name ?? ''),
    subtitle:
      (typeof customer.phone === 'string' && customer.phone) ||
      (typeof customer.email === 'string' && customer.email) ||
      undefined,
    type: 'customer' as const,
    searchValue: String(customer.full_name ?? customer.name ?? ''),
  }));

  const allResults: SearchResultItem[] = [...productResults, ...customerResults];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (item: SearchResultItem) => {
    setQuery('');
    setIsOpen(false);
    const baseUrl = item.type === 'product' ? ADMIN_ROUTES.PRODUCTS
      : item.type === 'customer' ? ADMIN_ROUTES.CUSTOMERS
      : ADMIN_ROUTES.ORDERS;
    navigate(`${baseUrl}?search=${encodeURIComponent(item.searchValue)}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const trimmed = query.trim();
      if (!trimmed) return;

      if (allResults.length > 0) {
        handleSelect(allResults[0]);
      } else {
        navigate(`/products?search=${encodeURIComponent(trimmed)}`);
        setIsOpen(false);
      }
    }

    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const showDropdown = isOpen && hasQuery;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <SearchIcon
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t('navSearchPlaceholder')}
          className="h-10 w-64 rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm text-slate-700 placeholder-slate-400 transition-colors focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 lg:w-80"
        />
      </div>

      {showDropdown && (
        <div className="absolute left-0 top-full mt-0.5 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          {isLoading && allResults.length === 0 && (
            <div className="px-4 py-3 text-sm text-slate-500">{t('searching')}</div>
          )}

          {!isLoading && allResults.length === 0 && hasError && (
            <div className="px-4 py-3 text-sm text-red-600">{t('searchFailed')}</div>
          )}

          {!isLoading && allResults.length === 0 && !hasError && (
            <div className="px-4 py-3 text-sm text-slate-500">{t('noResults')}</div>
          )}

          {allResults.length > 0 && (
            <ul className="max-h-80 overflow-y-auto py-1">
              {productResults.length > 0 && (
                <>
                  <li className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
                    {t('products')}
                  </li>
                  {productResults.map((item) => (
                    <li
                      key={`product-${item.id}`}
                      onClick={() => handleSelect(item)}
                      className="flex cursor-pointer items-center gap-3 px-4 py-2 hover:bg-slate-50"
                    >
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.label}
                          className="h-8 w-8 shrink-0 rounded-lg object-cover"
                          onError={(e) => {
                            // Ảnh hỏng/404 thì ẩn hẳn, để lại ô trống thay vì icon vỡ.
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-xs font-medium text-blue-700">
                          P
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-900">{item.label}</p>
                        <p className="truncate text-xs text-slate-500">{item.subtitle}</p>
                      </div>
                    </li>
                  ))}
                </>
              )}

              {customerResults.length > 0 && (
                <>
                  <li className="border-t border-slate-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
                    {t('customers')}
                  </li>
                  {customerResults.map((item) => (
                    <li
                      key={`customer-${item.id}`}
                      onClick={() => handleSelect(item)}
                      className="flex cursor-pointer items-center gap-3 px-4 py-2 hover:bg-slate-50"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-100 text-xs font-medium text-green-700">
                        C
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-900">{item.label}</p>
                        {item.subtitle && (
                          <p className="truncate text-xs text-slate-500">{item.subtitle}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
